import {
  type AffineThemeKeyV2,
  type ThemeName,
  themesV2,
} from '@streamlain/theme/v2';

import {
  edgelessCanvasBackgroundVar,
  edgelessCanvasGridColorVar,
} from './minimap-panel';

export interface EdgelessLineGridPalette {
  readonly cell: string;
  readonly block: string;
  readonly sector: string;
}

// Separate line-grid tokens keep existing dot/cross colors unchanged.
export const edgelessCanvasLineGridColorVars = {
  cell: '--streamlain-edgeless-canvas-grid-cell-color',
  block: '--streamlain-edgeless-canvas-grid-block-color',
  sector: '--streamlain-edgeless-canvas-grid-sector-color',
} as const;

/** Colours of the world chrome: the axes and the area outside the world. */
export const edgelessCanvasAxisColorVar =
  '--streamlain-edgeless-canvas-axis-color';
export const edgelessCanvasOutsideColorVar =
  '--streamlain-edgeless-canvas-outside-color';

/** The theme roles behind each line-grid tier. */
export const edgelessCanvasLineGridRoles = {
  cell: 'edgeless/gridCell',
  block: 'edgeless/gridBlock',
  sector: 'edgeless/gridSector',
} as const satisfies Record<keyof EdgelessLineGridPalette, AffineThemeKeyV2>;

const lineGridLevels = Object.keys(
  edgelessCanvasLineGridRoles
) as (keyof EdgelessLineGridPalette)[];

/**
 * The canvas colours of one theme as CSS declarations. A document can pin its
 * canvas to a theme other than the app's, so the values come from that
 * theme's table instead of the app-scoped `--streamlain-v2-*` variables.
 */
export function edgelessCanvasThemeStyle(theme: ThemeName) {
  const roles = themesV2[theme];
  return [
    [edgelessCanvasBackgroundVar, roles['edgeless/canvas']],
    [edgelessCanvasGridColorVar, roles['edgeless/grid']],
    [edgelessCanvasAxisColorVar, roles['edgeless/axis']],
    [edgelessCanvasOutsideColorVar, roles['edgeless/outside']],
    ...lineGridLevels.map(level => [
      edgelessCanvasLineGridColorVars[level],
      roles[edgelessCanvasLineGridRoles[level]],
    ]),
  ]
    .map(([name, value]) => `${name}: ${value};`)
    .join('\n');
}
